'use client';

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from '@/components/Link';
import { motionFadeUp } from '@/styles/motionAnimations';

const motionAnimation = { ...motionFadeUp, transition: { ...motionFadeUp.transition, duration: 0.5 } };
const DEFAULT_DELAY_IN_SECONDS = 5;

export type BannerDeal = {
    id: string;
    title: string;
    href: string;
    badgeText?: string;
    hasCta?: boolean;
};

export type DealsBannerScrollProps = {
    delaySeconds?: number;
    deals: BannerDeal[];
};

export function DealsBannerScroll({ delaySeconds = DEFAULT_DELAY_IN_SECONDS, deals }: DealsBannerScrollProps) {
    const [index, setIndex] = useState(0);
    const timeoutRef = useRef<NodeJS.Timeout>(undefined);
    const hoverRef = useRef(false);
    const maxIndex = deals.length - 1;
    const delay = (delaySeconds > 0 ? delaySeconds : DEFAULT_DELAY_IN_SECONDS) * 1000;

    const onHoverStart = () => {
        hoverRef.current = true;
    };
    const onHoverEnd = () => {
        hoverRef.current = false;
    };

    useEffect(() => {
        clearTimeout(timeoutRef.current);
        const handler = () => {
            if (hoverRef.current) {
                timeoutRef.current = setTimeout(handler, 100);
                return;
            }

            setIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
            timeoutRef.current = setTimeout(handler, delay);
        };
        timeoutRef.current = setTimeout(handler, delay);

        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, [delay, maxIndex]);

    const deal = deals[index];

    return (
        <AnimatePresence mode="popLayout" initial={false}>
            <motion.div key={deal.id} className="flex lg:justify-center" onHoverStart={onHoverStart} onHoverEnd={onHoverEnd} {...motionAnimation}>
                <Link href={deal.href} className="group flex items-center gap-2 lg:gap-3 text-xs font-bold leading-tight">
                    {deal.badgeText ? <span className="shrink-0 rounded bg-white px-1.5 py-0.5 text-[10px] uppercase text-primary">{deal.badgeText}</span> : null}
                    <span className="line-clamp-2 underline lg:no-underline lg:group-hover:underline">
                        {deal.title}
                        <span aria-hidden className="lg:hidden">
                            {' →'}
                        </span>
                    </span>
                    {deal.hasCta ? <span className="hidden lg:inline-flex rounded-full bg-white px-3 py-1 text-primary">Learn more</span> : null}
                </Link>
            </motion.div>
        </AnimatePresence>
    );
}
